import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleGenAI, Chat, GenerateContentResponse, FunctionDeclaration, Type } from '@google/genai';
import { BotConfig } from '../types';

interface Message {
  role: 'user' | 'model';
  text: string;
}

const WEBHOOK_TOOL_NAME = 'consult_webhook';

const webhookToolDeclaration: FunctionDeclaration = {
  name: WEBHOOK_TOOL_NAME,
  description: 'Consult an external system for information or actions not in your knowledge base. Use this whenever the user asks for real-time data or specific external information.',
  parameters: {
    type: Type.OBJECT,
    properties: {
      query: {
        type: Type.STRING,
        description: 'The query or keyword to send to the external webhook.',
      },
    },
    required: ['query'],
  },
};

export const useChat = (config: BotConfig, apiKey?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatSessionRef = useRef<Chat | null>(null);

  // Reset chat when configuration changes
  useEffect(() => {
    chatSessionRef.current = null;
    setMessages([]);
  }, [config.systemInstruction, config.webhookUrl, config.googleSheets]);

  const sendMessage = useCallback(async (text: string) => {
    const key = apiKey || process.env.API_KEY;
    
    if (!text.trim()) return;

    if (!key) {
      console.error("API Key is missing. Please check your Vercel Environment Variables.");
      setMessages((prev) => [...prev, { role: 'user', text }]);
      setTimeout(() => {
          setMessages((prev) => [...prev, { role: 'model', text: "Configuration Error: API Key is missing. Please ensure 'API_KEY' is set in your Vercel Environment Variables." }]);
      }, 500);
      return;
    }

    // Optimistic update
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setIsLoading(true);

    try {
      if (!chatSessionRef.current) {
        const ai = new GoogleGenAI({ apiKey: key });
        
        // Determine Tools configuration
        // Rules: If googleSheets are present, we MUST use `googleSearch`.
        // The prompt rules state "Only tools: googleSearch is permitted. Do not use it with other tools."
        // So if Sheets are used, we disable the Webhook tool to prevent conflicts.
        let tools: any[] = [];
        let modifiedInstruction = config.systemInstruction;

        if (config.googleSheets && config.googleSheets.length > 0) {
            tools = [{ googleSearch: {} }];
            modifiedInstruction += `\n\n[REFERENCE MATERIALS]: You have access to the following Google Sheets. Use your 'google_search' tool to access their content if they are public:\n${config.googleSheets.map(url => `- ${url}`).join('\n')}`;
        } else if (config.webhookUrl) {
            tools = [{ functionDeclarations: [webhookToolDeclaration] }];
        }
        
        chatSessionRef.current = ai.chats.create({
          model: 'gemini-2.5-flash',
          config: {
            systemInstruction: modifiedInstruction,
            tools: tools,
          },
        });
      }

      // Initial message to model
      let result = await chatSessionRef.current.sendMessageStream({ message: text });
      let fullResponse = '';
      let functionCallFound = null;

      // Add placeholder for model response
      setMessages((prev) => [...prev, { role: 'model', text: '' }]);

      // Process Stream
      for await (const chunk of result) {
        const c = chunk as GenerateContentResponse;
        
        // Accumulate text
        if (c.text) {
          fullResponse += c.text;
          setMessages((prev) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'model') {
              lastMessage.text = fullResponse;
            }
            return newMessages;
          });
        }
        
        // Detect function call (Webhook only)
        if (c.candidates?.[0]?.content?.parts) {
            for (const part of c.candidates[0].content.parts) {
                if (part.functionCall) {
                    functionCallFound = part.functionCall;
                }
            }
        }
      }

      // If function call detected (Webhook), execute it and send result back
      if (functionCallFound && config.webhookUrl && (!config.googleSheets || config.googleSheets.length === 0)) {
        const fc = functionCallFound;
         if (fc.name === WEBHOOK_TOOL_NAME) {
            // Show loading state for webhook
            setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'model') {
                    lastMessage.text = lastMessage.text + "\n(Consulting external system...)";
                }
                return newMessages;
            });

            let functionResult = "Error";
            try {
                const response = await fetch(config.webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ query: (fc.args as any).query })
                });
                const data = await response.json();
                functionResult = JSON.stringify(data);
            } catch (e: any) {
                functionResult = JSON.stringify({ error: e.message });
            }

            // Send tool response back to model
            const toolResponseParts = [
                {
                    functionResponse: {
                        name: fc.name,
                        id: fc.id || 'unknown_id',
                        response: { result: functionResult }
                    }
                }
            ];

            // Get final answer
            // Casting message to any to bypass strict type check on the frontend for Part[]
            result = await chatSessionRef.current.sendMessageStream({ message: toolResponseParts as any });
            
            // Clean up the "(Consulting...)" text for the final streaming response
             setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'model') {
                    lastMessage.text = fullResponse; // Reset to what we had before the "Thinking"
                }
                return newMessages;
            });

            for await (const chunk of result) {
                const c = chunk as GenerateContentResponse;
                if (c.text) {
                    fullResponse += c.text;
                    setMessages((prev) => {
                        const newMessages = [...prev];
                        const lastMessage = newMessages[newMessages.length - 1];
                        if (lastMessage.role === 'model') {
                            lastMessage.text = fullResponse;
                        }
                        return newMessages;
                    });
                }
            }
         }
      }

    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [...prev, { role: 'model', text: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  }, [config.systemInstruction, config.webhookUrl, config.googleSheets, apiKey]);

  return {
    messages,
    sendMessage,
    isLoading
  };
};